import React from 'react';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { 
  TestDefinition, 
  LabTestDefinition, 
  ImagingTestDefinition,
  ProcedureTestDefinition,
  SurgicalTestDefinition,
  PhysicalExamTestDefinition,
  ReferralTestDefinition,
  GenericTestDefinition,
  InterventionTestDefinition,
  RiskAssessmentTestDefinition
} from '../../../types/procedures';
import { OrderCode } from '../utils/orderUtils';
import { OrderType } from '../../../types/enums';
import { NPIProviderSearchField } from './NPIProviderSearchField';

interface OrderTypeFieldsProps {
  orderType: string;
  orderCode: OrderCode | TestDefinition | null;
  setOrderCode: (code: OrderCode | TestDefinition | null) => void;
  specimenType: string;
  setSpecimenType: (type: string) => void;
  fastingRequired: boolean;
  setFastingRequired: (required: boolean) => void;
  collectionTime: string;
  setCollectionTime: (time: string) => void;
  bodySite: string;
  setBodySite: (site: string) => void;
  laterality: string;
  setLaterality: (laterality: string) => void;
  selectedCheckups: string[];
  setSelectedCheckups: (checkups: string[]) => void;
  testDefinitions: TestDefinition[];
  modalMode: 'edit' | 'view';
  CHECKUP_OPTIONS: { code: string; label: string }[];
  // Referral-specific props
  selectedExternalProvider: any | null;
  setSelectedExternalProvider: (provider: any | null) => void;
}

export const OrderTypeFields: React.FC<OrderTypeFieldsProps> = ({
  orderType,
  orderCode,
  setOrderCode,
  specimenType,
  setSpecimenType,
  fastingRequired,
  setFastingRequired,
  collectionTime,
  setCollectionTime,
  bodySite,
  setBodySite,
  laterality,
  setLaterality,
  selectedCheckups,
  setSelectedCheckups,
  testDefinitions,
  modalMode,
  CHECKUP_OPTIONS,
  // Referral-specific props
  selectedExternalProvider,
  setSelectedExternalProvider,
}) => {
  if (orderType === OrderType.LAB) {
    return (
      <>
        <TextField
          label="Specimen Type"
          value={specimenType}
          onChange={e => modalMode === 'edit' && setSpecimenType(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{ readOnly: modalMode === 'view' }}
        />
        <FormControlLabel
          control={
            <Checkbox 
              checked={!!fastingRequired} 
              onChange={e => modalMode === 'edit' && setFastingRequired(e.target.checked)} 
              disabled={modalMode === 'view'} 
            />
          }
          label="Fasting Required"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Collection Time"
          value={collectionTime}
          onChange={e => modalMode === 'edit' && setCollectionTime(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{ readOnly: modalMode === 'view' }}
        />
      </>
    );
  }

  if (orderType === OrderType.IMAGING) {
    return (
      <>
        <TextField
          label="Body Site"
          value={bodySite}
          onChange={e => modalMode === 'edit' && setBodySite(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{ readOnly: modalMode === 'view' }}
        />
        <TextField
          label="Laterality"
          value={laterality}
          onChange={e => modalMode === 'edit' && setLaterality(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{ readOnly: modalMode === 'view' }}
        />
      </>
    );
  }

  // Add support for other order types
  if (orderType === OrderType.PROCEDURE) {
    return (
      <>
        <TextField
          label="Body Site"
          value={bodySite}
          onChange={e => modalMode === 'edit' && setBodySite(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{ readOnly: modalMode === 'view' }}
        />
      </>
    );
  }

  if (orderType === OrderType.SURGICAL) {
    return (
      <>
        <TextField
          label="Surgical Procedure Type"
          value={bodySite} // Reusing bodySite field temporarily for procedure type
          onChange={e => modalMode === 'edit' && setBodySite(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          placeholder="e.g., Laparoscopic Cholecystectomy"
          InputProps={{ readOnly: modalMode === 'view' }}
        />
        <TextField
          label="Surgical Specialty"
          value={laterality} // Reusing laterality field temporarily for specialty
          onChange={e => modalMode === 'edit' && setLaterality(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          placeholder="e.g., General Surgery, Orthopedic Surgery"
          InputProps={{ readOnly: modalMode === 'view' }}
        />
        <TextField
          label="Estimated Duration (minutes)"
          value={collectionTime} // Reusing collectionTime field temporarily for duration
          onChange={e => modalMode === 'edit' && setCollectionTime(e.target.value)}
          fullWidth
          type="number"
          sx={{ mb: 2 }}
          InputProps={{ readOnly: modalMode === 'view' }}
        />
        <FormControlLabel
          control={
            <Checkbox 
              checked={!!fastingRequired} 
              onChange={e => modalMode === 'edit' && setFastingRequired(e.target.checked)} 
              disabled={modalMode === 'view'} 
            />
          }
          label="Requires Consent"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Special Instructions"
          value={specimenType} // Reusing specimenType field temporarily for instructions
          onChange={e => modalMode === 'edit' && setSpecimenType(e.target.value)}
          fullWidth
          multiline
          rows={3}
          sx={{ mb: 2 }}
          placeholder="Pre-op instructions, special equipment, etc."
          InputProps={{ readOnly: modalMode === 'view' }}
        />
      </>
    );
  }

  if (orderType === OrderType.PHYSICAL_EXAM) {
    return (
      <>
        {/* Checkup selection for Physical Exam orders */}
        <Autocomplete
          multiple
          options={CHECKUP_OPTIONS}
          getOptionLabel={(option) => option.label}
          value={CHECKUP_OPTIONS.filter(opt => selectedCheckups.includes(opt.code))}
          onChange={(_, newValue) => {
            if (modalMode === 'edit') {
              setSelectedCheckups(newValue.map(opt => opt.code));
            }
          }}
          renderInput={params => (
            <TextField 
              {...params} 
              label="Checkup Items" 
              placeholder="Select checkup items..."
              fullWidth 
              sx={{ mb: 2 }} 
              InputProps={{ ...params.InputProps, readOnly: modalMode === 'view' }} 
            />
          )}
          disabled={modalMode === 'view'}
        />
      </>
    );
  }

  if (orderType === OrderType.REFERRAL) {
    return (
      <>
        <NPIProviderSearchField
          value={selectedExternalProvider}
          onChange={(provider) => {
            if (modalMode === 'edit') {
              setSelectedExternalProvider(provider);
            }
          }}
          label="Refer to External Provider/Facility"
          placeholder="Search by provider name, organization name, or NPI number..."
          disabled={modalMode === 'view'}
          fullWidth
          sx={{ mb: 2 }}
        />
      </>
    );
  }

  // For other order types (GENERIC, INTERVENTION, RISK_ASSESSMENT), 
  // no additional type-specific fields are needed beyond the main Test Code selection
  return null;
}; 